import { useState } from 'react';
import { getCPFFormatado, getCNPJFormatado, validarCPF, validarCNPJ, getMascaraMoeda, getFloat } from 'utils/uteis';

export interface Produto {
  id: string;
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

export interface SecaoAquisicao {
  titulo: string;
  produtosCnpj: Produto[];
  produtosCpf: Produto[];
}

interface NovoProduto {
  nomeRazao: string;
  documento: string;
  valor: string;
  valorNumerico: number;
}

interface NovosProdutos {
  cnpj: NovoProduto;
  cpf: NovoProduto;
}

// Pure helper functions (exported for testing)
export const formatarValorParaExibicaoFn = (valor: number | string | null | undefined) => {
  if (!valor && valor !== 0) return '';
  const numeroValor = typeof valor === 'string' ? parseFloat(valor) : valor;
  if (isNaN(numeroValor)) return '';
  return numeroValor.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatarValorInputFn = (valor: string, getMascaraMoedaFn: (v: number) => string) => {
  const apenasDigitos = valor.replace(/\D/g, '');
  if (apenasDigitos === '') return '';
  return getMascaraMoedaFn(parseInt(apenasDigitos, 10) / 100);
};

export const getSecaoKeyFn = (secao: SecaoAquisicao, index: number) => {
  return secao.titulo.replace(/\s+/g, '-').toLowerCase() || `secao-${index}`;
};

export const calcularTotalSecaoFn = (secao: SecaoAquisicao) => {
  const totalCnpj = secao.produtosCnpj.reduce((total, produto) => total + (produto.valorNumerico || 0), 0);
  const totalCpf = secao.produtosCpf.reduce((total, produto) => total + (produto.valorNumerico || 0), 0);
  return totalCnpj + totalCpf;
};

export const calcularTotalGeralFn = (secoes: SecaoAquisicao[]) => {
  return secoes.reduce((total, secao) => total + calcularTotalSecaoFn(secao), 0);
};

export const isFormValidFn = (secoes: SecaoAquisicao[], formikValues: any) => {
  const hasAquisicoes = secoes.some(secao =>
    secao.produtosCnpj.length > 0 || secao.produtosCpf.length > 0
  );

  const totalRecursosAF =
    Number(formikValues.alimentacao_af_recurso_pnae || 0) +
    Number(formikValues.alimentacao_af_recurso_estado || 0) +
    Number(formikValues.alimentacao_af_recurso_prefeitura || 0);

  const totalAquisicoes = calcularTotalGeralFn(secoes);

  return hasAquisicoes && totalAquisicoes <= totalRecursosAF && totalRecursosAF > 0;
};

export const criarSecoesComProdutoAdicionado = (
  secoes: SecaoAquisicao[],
  secaoIndex: number,
  tipo: 'cnpj' | 'cpf',
  produto: { nomeRazao: string; documento: string; valor: string; valorNumerico: number },
  validator?: (doc: string) => boolean
) => {
  if (!produto || !produto.nomeRazao || produto.valorNumerico <= 0) return secoes;

  const validatorFn = validator ?? (tipo === 'cnpj' ? validarCNPJ : validarCPF);
  const ehValido = validatorFn(produto.documento);
  if (!ehValido) return secoes;

  const newProduto: Produto = {
    id: Math.random().toString(36).substring(2, 11),
    nomeRazao: produto.nomeRazao.trim(),
    documento: produto.documento,
    valor: produto.valor,
    valorNumerico: produto.valorNumerico,
  };

  const updated = secoes.map((s, idx) => {
    if (idx !== secaoIndex) return { ...s, produtosCnpj: [...s.produtosCnpj], produtosCpf: [...s.produtosCpf] };
    return {
      ...s,
      produtosCnpj: tipo === 'cnpj' ? [...s.produtosCnpj, newProduto] : [...s.produtosCnpj],
      produtosCpf: tipo === 'cpf' ? [...s.produtosCpf, newProduto] : [...s.produtosCpf],
    };
  });

  return updated;
};

interface UseSchoolFeedingAcquisitionsProps {
  initialData: SecaoAquisicao[];
  formik?: any;
  onSecoesChange?: (secoes: SecaoAquisicao[]) => void;
}

export const useSchoolFeedingAcquisitions = ({
  initialData,
  formik,
  onSecoesChange
}: UseSchoolFeedingAcquisitionsProps) => {
  const [secoes, setSecoes] = useState<SecaoAquisicao[]>(initialData);
  const [novosProdutos, setNovosProdutos] = useState<NovosProdutos[]>([
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } },
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } },
    { cnpj: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 }, cpf: { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 } }
  ]);

  const formatarValorInput = (valor: string) => {
    const apenasDigitos = valor.replace(/\D/g, '');
    if (apenasDigitos === '') return '';
    return getMascaraMoeda(parseInt(apenasDigitos, 10) / 100);
  };

  const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
    return formatarValorParaExibicaoFn(valor);
  };

  const getSecaoKey = (secao: SecaoAquisicao, index: number) => {
    return getSecaoKeyFn(secao, index);
  };

  const calcularTotalSecao = (secao: SecaoAquisicao) => {
    return calcularTotalSecaoFn(secao);
  };

  const calcularTotalGeral = () => {
    return calcularTotalGeralFn(secoes);
  };

  const handleAddProduto = (secaoIndex: number, tipo: 'cnpj' | 'cpf') => {
    const produto = novosProdutos[secaoIndex][tipo];
    const updated = criarSecoesComProdutoAdicionado(secoes, secaoIndex, tipo, produto as any);

    if (updated !== secoes) {
      setSecoes(updated);

      // Sincronizar com formik se disponível
      if (formik) {
        formik.setFieldValue('secoesAquisicao', updated);
      }

      // Callback opcional
      if (onSecoesChange) {
        onSecoesChange(updated);
      }

      handleClearFields(secaoIndex, tipo);
    }
  };

  const handleClearFields = (secaoIndex: number, tipo: 'cnpj' | 'cpf') => {
    setNovosProdutos(prev => {
      const updated = [...prev];
      updated[secaoIndex][tipo] = { nomeRazao: '', documento: '', valor: '', valorNumerico: 0 };
      return updated;
    });
  };

  const handleInputChange = (secaoIndex: number, tipo: 'cnpj' | 'cpf', field: string, value: string) => {
    setNovosProdutos(prev => {
      const updated = [...prev];

      if (field === 'documento') {
        const formattedValue = tipo === 'cnpj'
          ? getCNPJFormatado(value.replace(/\D/g, ''))
          : getCPFFormatado(value.replace(/\D/g, ''));

        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          documento: formattedValue
        };
      } else if (field === 'valor') {
        const valorFormatado = formatarValorInput(value);
        const valorNumerico = getFloat(valorFormatado);

        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          valor: valorFormatado,
          valorNumerico
        };
      } else {
        updated[secaoIndex][tipo] = {
          ...updated[secaoIndex][tipo],
          [field]: value
        };
      }

      return updated;
    });
  };

  const handleRemoveProdutoCnpj = (secaoIndex: number, produtoId: string) => {
    const newSecoes = [...secoes];
    newSecoes[secaoIndex].produtosCnpj = newSecoes[secaoIndex].produtosCnpj.filter(p => p.id !== produtoId);
    setSecoes(newSecoes);

    // Sincronizar com formik se disponível
    if (formik) {
      formik.setFieldValue('secoesAquisicao', newSecoes);
    }

    // Callback opcional
    if (onSecoesChange) {
      onSecoesChange(newSecoes);
    }
  };

  const handleRemoveProdutoCpf = (secaoIndex: number, produtoId: string) => {
    const newSecoes = [...secoes];
    newSecoes[secaoIndex].produtosCpf = newSecoes[secaoIndex].produtosCpf.filter(p => p.id !== produtoId);
    setSecoes(newSecoes);

    // Sincronizar com formik se disponível
    if (formik) {
      formik.setFieldValue('secoesAquisicao', newSecoes);
    }

    // Callback opcional
    if (onSecoesChange) {
      onSecoesChange(newSecoes);
    }
  };

  return {
    secoes,
    setSecoes,
    novosProdutos,
    formatarValorInput,
    formatarValorParaExibicao,
    getSecaoKey,
    calcularTotalSecao,
    calcularTotalGeral,
    handleAddProduto,
    handleClearFields,
    handleInputChange,
    handleRemoveProdutoCnpj,
    handleRemoveProdutoCpf,
  };
};
